/**
 * ForgotPasswordPage — email submission for password reset
 *
 * URL: /auth/forgot-password
 *
 * Two states:
 *  1. Form — email input + submit button
 *  2. Confirmation — always shown after submit regardless of whether the
 *     email is registered (prevents email enumeration)
 *
 * Visual shell mirrors LoginPage (split-screen, brand-glow, surface-raised-2
 * right panel) for a coherent auth system.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { requestPasswordReset } from '@/services/authApi';
import Button from '@/components/Button';
import { getErrorMessage } from '@/utils/errors';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await requestPasswordReset(email);
      // Always show confirmation regardless of whether email exists.
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .mc-submit-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .mc-submit-btn:not(:disabled):hover {
          background-color: var(--color-brand-primary-hover);
          box-shadow: 0 0 18px rgba(59, 130, 246, 0.30), 0 4px 12px rgba(0, 0, 0, 0.20);
        }
        .mc-submit-btn:not(:disabled):active {
          transform: scale(0.98);
        }
        .mc-back-link {
          color: var(--color-text-muted);
          text-decoration: none;
          font-size: 13px;
          transition: color 150ms ease;
        }
        .mc-back-link:hover {
          color: var(--color-text-secondary);
        }
      `}</style>

      {/* Full-screen split layout */}
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          backgroundColor: 'var(--color-surface-base)',
        }}
      >
        {/* ── Left panel (desktop only) ─────────────────────────────────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width: '50%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface-base)',
            padding: '64px 56px',
          }}
        >
          {/* Radial brand-glow anchored upper-right */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '560px',
              height: '560px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          {/* Secondary soft glow, lower-left */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-40px',
              width: '360px',
              height: '360px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />

          {/* Left panel content */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '28px',
              maxWidth: '380px',
            }}
          >
            <img
              src="/pwa-512.png"
              alt="PointeTracer logo"
              style={{
                width: '280px',
                height: '280px',
                objectFit: 'contain',
                filter:
                  'drop-shadow(0 0 24px rgba(59,130,246,0.45)) drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h1
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                Forgot your password?
              </h1>
              <p
                className="text-body-md"
                style={{ color: 'var(--color-text-secondary)', margin: 0 }}
              >
                We'll send you a secure link to reset it.
              </p>
            </div>
          </div>
        </div>

        {/* ── Right panel (form) ────────────────────────────────────────────── */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            backgroundColor: 'var(--color-surface-raised-2)',
          }}
          className="lg:w-1/2"
        >
          <div className="w-full" style={{ maxWidth: '400px' }}>
            {/* Mobile-only logo */}
            <div
              className="flex lg:hidden"
              style={{ justifyContent: 'center', marginBottom: '32px' }}
            >
              <img
                src="/pwa-512.png"
                alt="PointeTracer logo"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 0 24px rgba(59,130,246,0.25)',
                }}
              />
            </div>

            {submitted ? (
              /* ── Confirmation state ─────────────────────────────────────── */
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(59, 130, 246, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                  }}
                >
                  <EnvelopeIcon
                    style={{ width: '28px', height: '28px', color: 'var(--color-brand-primary)' }}
                  />
                </div>
                <h2
                  className="text-display-lg"
                  style={{
                    color: 'var(--color-text-primary)',
                    margin: '0 0 12px',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Check your email
                </h2>
                <p
                  className="text-body-md"
                  style={{
                    color: 'var(--color-text-secondary)',
                    margin: '0 0 32px',
                    lineHeight: 1.6,
                  }}
                >
                  If an account exists for <strong>{email}</strong>, a password reset link has
                  been sent. The link expires in 1 hour.
                </p>
                <p className="text-body-sm" style={{ color: 'var(--color-text-muted)', margin: '0 0 24px' }}>
                  Didn't receive it? Check your spam folder, or{' '}
                  <button
                    onClick={() => setSubmitted(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-brand-primary)',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    try again
                  </button>
                  .
                </p>
                <Link to="/login" className="mc-back-link">
                  Back to sign in
                </Link>
              </div>
            ) : (
              /* ── Form state ─────────────────────────────────────────────── */
              <>
                <div style={{ marginBottom: '40px' }}>
                  <h2
                    className="text-display-lg"
                    style={{
                      color: 'var(--color-text-primary)',
                      margin: '0 0 8px',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Reset password
                  </h2>
                  <p
                    className="text-body-md"
                    style={{ color: 'var(--color-text-secondary)', margin: 0 }}
                  >
                    Enter your email and we'll send you a reset link.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  noValidate
                  style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                >
                  {/* Error banner */}
                  {error && (
                    <div
                      role="alert"
                      style={{
                        borderRadius: '10px',
                        border: '1px solid rgba(248, 113, 113, 0.30)',
                        backgroundColor: 'rgba(248, 113, 113, 0.08)',
                        padding: '12px 14px',
                        fontSize: '13px',
                        color: 'var(--color-signal-bad)',
                        lineHeight: 1.5,
                      }}
                    >
                      {error}
                    </div>
                  )}

                  {/* Email field */}
                  <div>
                    <label
                      htmlFor="forgot-email"
                      className="text-label"
                      style={{
                        display: 'block',
                        color: 'var(--color-text-muted)',
                        marginBottom: '6px',
                      }}
                    >
                      Email
                    </label>
                    <div style={{ position: 'relative' }}>
                      <EnvelopeIcon
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '16px',
                          height: '16px',
                          color: 'var(--color-text-muted)',
                          pointerEvents: 'none',
                        }}
                      />
                      <input
                        type="email"
                        id="forgot-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        autoComplete="email"
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          paddingLeft: '36px',
                          paddingRight: '12px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                          backgroundColor: 'var(--color-surface-raised-3)',
                          border: '1px solid var(--color-border-subtle)',
                          borderRadius: '8px',
                          color: 'var(--color-text-primary)',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>

                  {/* Submit button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting || !email.trim()}
                    className="mc-submit-btn"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'var(--color-brand-primary)',
                      color: 'var(--color-text-inverse)',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff',
                            borderRadius: '50%',
                            animation: 'spin 0.7s linear infinite',
                          }}
                        />
                        Sending…
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </Button>

                  {/* Back to login */}
                  <div style={{ textAlign: 'center' }}>
                    <Link to="/login" className="mc-back-link">
                      Back to sign in
                    </Link>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
